import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';

import {
  ListRegistrationItem,
  ListEventInfo,
  ListStudentName,
  ListEventMeta,
  ListRemoveLink,
  ListEmptyState,
} from './EventRegistrationStyles';

const MainHeader = styled.h4`
  margin: 0;
  font-size: 24px;
`;

const Divider = styled.hr`
  margin: 12px 0 20px 0;
  border-top: 1px solid #ddd;
`;

const Wrapper = styled.div`
  margin-top: 0;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 32px;
`;

const BackLink = styled(Link)`
  font-size: 14px;
  color: #479de6;
  display: inline-block;
  margin-bottom: 16px;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionHeader = styled.h5`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const SectionDivider = styled.div`
  margin: 24px 0 16px 0;
  border-top: 1px solid #eee;
`;

const AllRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await axios.get('/api/v2/event-registrations');
        setRegistrations(response.data.registrations || []);
      } catch (err) {
        setError('Failed to load registrations');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, []);

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this registration?')) return;

    try {
      await axios.delete(`/api/v2/event-registrations/${id}`);
      setRegistrations((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error('Failed to remove registration:', err);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = registrations.filter(
    (r) => !r.eventDate || r.eventDate >= today,
  );
  const past = registrations.filter((r) => r.eventDate && r.eventDate < today);

  const renderItem = (reg, muted = false) => {
    const dateDisplay = reg.eventDate
      ? new Date(reg.eventDate + 'T00:00').toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

    return (
      <ListRegistrationItem key={reg.id}>
        <ListEventInfo>
          <ListStudentName muted={muted}>
            {reg.studentFirstName} {reg.studentLastName}
          </ListStudentName>
          <ListEventMeta muted={muted}>
            {reg.eventName}
            {reg.venueName ? ` — ${reg.venueName}` : ''}
            {dateDisplay ? ` | ${dateDisplay}` : ''}
          </ListEventMeta>
        </ListEventInfo>
        {!muted && (
          <ListRemoveLink onClick={() => handleRemove(reg.id)}>
            Remove
          </ListRemoveLink>
        )}
      </ListRegistrationItem>
    );
  };

  return (
    <div className="bnl-dashboard-container">
      <div className="bnl-dashboard">
        <BackLink to="/my-account">← Back to My Account</BackLink>
        <MainHeader>Event Registrations</MainHeader>
        <Divider />
        {loading && <p>Loading registrations...</p>}
        {error && <p className="text-danger">{error}</p>}
        {!loading && !error && (
          <Wrapper>
            <SectionHeader>Upcoming</SectionHeader>
            {upcoming.length === 0 ? (
              <ListEmptyState>No upcoming registrations.</ListEmptyState>
            ) : (
              upcoming.map((reg) => renderItem(reg, false))
            )}

            {past.length > 0 && (
              <>
                <SectionDivider />
                <SectionHeader>Past</SectionHeader>
                {past.map((reg) => renderItem(reg, true))}
              </>
            )}
          </Wrapper>
        )}
      </div>
    </div>
  );
};

export default AllRegistrations;
